/**
 * One conversation's native catalogue facts, as a React hook.
 *
 * The harness panel's seat occupants are handed a session id by the harness and read everything else from
 * the session plane's own catalogue, so they depend on nothing but the kernel. One subscription to the catalogue
 * serves every occupant.
 */
import { useSyncExternalStore } from "react";
import { watchDshCatalogue, type Catalogue, type CatalogueSession } from "./session-bridge";

let current: Catalogue | undefined;
const listeners = new Set<() => void>();
let stop: (() => void) | undefined;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  stop ??= watchDshCatalogue(catalogue => {
    current = catalogue;
    for (const notify of listeners) notify();
  });
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) { stop?.(); stop = undefined; }
  };
}

/** The catalogue's summary of one conversation, or undefined until the catalogue lists it. */
export function useCatalogueSession(sessionId: string | undefined): CatalogueSession | undefined {
  return useSyncExternalStore(subscribe, () => sessionId === undefined ? undefined : current?.summaries.get(sessionId), () => undefined);
}
